import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import adminApi from '../../../../api/adminApi';
import '../../../../assets/css/admin/header.css';

const Header = ({ toggleSidebar }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const navigate = useNavigate();
    const searchWrapperRef = useRef(null);

    const storedAdmin = JSON.parse(localStorage.getItem('admin_data') || '{}');

    const adminData = {
        name: storedAdmin.name || 'Admin',
        email: storedAdmin.email || '',
        role: storedAdmin.role || 'Admin',
        phone: storedAdmin.phone || '',
        location: storedAdmin.location || '',
        employeeId: storedAdmin.employee_id || '',
        department: storedAdmin.department || ''
    };

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const timeAgo = (dateString) => {
        const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} min ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} ago`;
    };

    const fetchNotifications = async () => {
        try {
            const res = await adminApi.get('/notifications');
            setNotifications(res.data.notifications || []);
            setUnreadCount(res.data.unread_count || 0);
        } catch (err) {
            console.error('Failed to fetch notifications', err);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    // Close the search dropdown when clicking outside of it
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced live search across patients, therapists and appointments
    useEffect(() => {
        const query = searchQuery.trim();

        if (query.length < 2) {
            setSearchResults(null);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        const timer = setTimeout(() => {
            adminApi
                .get('/search', { params: { q: query } })
                .then((res) => {
                    setSearchResults(res.data);
                    setShowSearchResults(true);
                })
                .catch((err) => {
                    console.error('Search failed', err);
                    setSearchResults(null);
                })
                .finally(() => setSearchLoading(false));
        }, 350);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const searchHitCount = searchResults
        ? searchResults.patients.length + searchResults.therapists.length + searchResults.appointments.length
        : 0;

    const goToResult = (type) => {
        setShowSearchResults(false);
        const term = searchQuery.trim();
        setSearchQuery('');
        if (type === 'patient') navigate('/admin/patients', { state: { search: term } });
        if (type === 'therapist') navigate('/admin/therapists', { state: { search: term } });
        if (type === 'appointment') navigate('/admin/appointments', { state: { search: term } });
    };

    const handleMarkAllRead = async () => {
        try {
            await adminApi.post('/notifications/mark-all-read');
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark notifications as read', err);
        }
    };

    const handleNotificationClick = async (notif) => {
        if (notif.is_read) return;
        try {
            await adminApi.patch(`/notifications/${notif.id}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark notification as read', err);
        }
    };

    const handleProfileClick = () => {
        setShowProfile(false);
        Swal.fire({
            html: `
                <div class="admin-profile-modal-content">
                    <div class="admin-profile-modal-header">
                        <div class="admin-profile-modal-avatar">
                            ${adminData.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div class="admin-profile-modal-header-info">
                            <h3 class="admin-profile-modal-name">${adminData.name}</h3>
                            <p class="admin-profile-modal-role">${adminData.role}</p>
                        </div>
                    </div>
                    <div class="admin-profile-modal-body">
                        <div class="admin-profile-modal-info-row">
                            <span class="admin-profile-modal-icon">📧</span>
                            <div class="admin-profile-modal-info-text">
                                <span class="admin-profile-modal-label">Email</span>
                                <span class="admin-profile-modal-value">${adminData.email}</span>
                            </div>
                        </div>
                        <div class="admin-profile-modal-info-row">
                            <span class="admin-profile-modal-icon">📱</span>
                            <div class="admin-profile-modal-info-text">
                                <span class="admin-profile-modal-label">Phone</span>
                                <span class="admin-profile-modal-value">${adminData.phone}</span>
                            </div>
                        </div>
                        <div class="admin-profile-modal-info-row">
                            <span class="admin-profile-modal-icon">📍</span>
                            <div class="admin-profile-modal-info-text">
                                <span class="admin-profile-modal-label">Location</span>
                                <span class="admin-profile-modal-value">${adminData.location}</span>
                            </div>
                        </div>
                        <div class="admin-profile-modal-info-row">
                            <span class="admin-profile-modal-icon">🏢</span>
                            <div class="admin-profile-modal-info-text">
                                <span class="admin-profile-modal-label">Department</span>
                                <span class="admin-profile-modal-value">${adminData.department}</span>
                            </div>
                        </div>
                        <div class="admin-profile-modal-info-row">
                            <span class="admin-profile-modal-icon">🆔</span>
                            <div class="admin-profile-modal-info-text">
                                <span class="admin-profile-modal-label">Employee ID</span>
                                <span class="admin-profile-modal-value">${adminData.employeeId}</span>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            showCloseButton: true,
            showCancelButton: true,
            confirmButtonText: 'View Full Profile',
            cancelButtonText: 'Close',
            confirmButtonColor: '#FF6F00',
            cancelButtonColor: '#6B7280',
            width: '480px',
            padding: '0',
            customClass: {
                popup: 'admin-swal-profile-popup',
                htmlContainer: 'admin-swal-profile-html',
                confirmButton: 'admin-swal-confirm-btn',
                cancelButton: 'admin-swal-cancel-btn'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                window.location.href = '/admin/profile';
            }
        });
    };

    const handleLogoutClick = () => {
        setShowProfile(false);
        Swal.fire({
            title: 'Logout Confirmation',
            html: '<p class="admin-logout-modal-text">Are you sure you want to logout? You will need to login again to access your account.</p>',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Logout',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            reverseButtons: true,
            customClass: {
                popup: 'admin-swal-logout-popup',
                confirmButton: 'admin-swal-logout-confirm-btn',
                cancelButton: 'admin-swal-logout-cancel-btn'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_data');
                Swal.fire({
                    title: 'Logged Out!',
                    text: 'You have been successfully logged out.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                }).then(() => {
                    navigate('/admin/login');
                });
            }
        });
    };

    return (
        <header className="admin-header">
            <div className="admin-header-left">
                <button className="admin-menu-toggle" onClick={toggleSidebar}>
                    ☰
                </button>
            </div>

            <div className="admin-header-center">
                <div className="admin-search-container" ref={searchWrapperRef}>
                    <span className="admin-search-icon">🔍</span>
                    <input
                        type="text"
                        className="admin-search-input"
                        placeholder="Search patients, therapists, appointments..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => { if (searchResults) setShowSearchResults(true); }}
                    />
                    {searchQuery.trim().length > 0 && (
                        <button
                            type="button"
                            className="admin-search-clear-btn"
                            onClick={() => { setSearchQuery(''); setSearchResults(null); setShowSearchResults(false); }}
                        >
                            ✕
                        </button>
                    )}

                    {showSearchResults && searchQuery.trim().length >= 2 && (
                        <div className="admin-dropdown admin-search-dropdown">
                            {searchLoading ? (
                                <div className="admin-search-empty">Searching…</div>
                            ) : searchHitCount === 0 ? (
                                <div className="admin-search-empty">No matches for “{searchQuery.trim()}”</div>
                            ) : (
                                <>
                                    {searchResults.patients.length > 0 && (
                                        <div className="admin-search-group">
                                            <div className="admin-search-group-label">Patients</div>
                                            {searchResults.patients.map((p) => (
                                                <div key={`p-${p.id}`} className="admin-search-result-item" onClick={() => goToResult('patient')}>
                                                    <span className="admin-search-result-icon">🧑‍⚕️</span>
                                                    <div className="admin-search-result-text">
                                                        <span className="admin-search-result-title">{p.title}</span>
                                                        <span className="admin-search-result-subtitle">{p.subtitle}</span>
                                                    </div>
                                                    {p.status && <span className="admin-search-result-status">{p.status}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {searchResults.therapists.length > 0 && (
                                        <div className="admin-search-group">
                                            <div className="admin-search-group-label">Therapists</div>
                                            {searchResults.therapists.map((t) => (
                                                <div key={`t-${t.id}`} className="admin-search-result-item" onClick={() => goToResult('therapist')}>
                                                    <span className="admin-search-result-icon">🩺</span>
                                                    <div className="admin-search-result-text">
                                                        <span className="admin-search-result-title">{t.title}</span>
                                                        <span className="admin-search-result-subtitle">{t.subtitle}</span>
                                                    </div>
                                                    {t.status && <span className="admin-search-result-status">{t.status}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {searchResults.appointments.length > 0 && (
                                        <div className="admin-search-group">
                                            <div className="admin-search-group-label">Appointments</div>
                                            {searchResults.appointments.map((a) => (
                                                <div key={`a-${a.id}`} className="admin-search-result-item" onClick={() => goToResult('appointment')}>
                                                    <span className="admin-search-result-icon">📅</span>
                                                    <div className="admin-search-result-text">
                                                        <span className="admin-search-result-title">{a.title}</span>
                                                        <span className="admin-search-result-subtitle">{a.subtitle}</span>
                                                    </div>
                                                    {a.status && <span className="admin-search-result-status">{a.status}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="admin-header-right">
                <div className="admin-notification-wrapper">
                    <button
                        className="admin-icon-btn"
                        onClick={() => setShowNotifications(!showNotifications)}
                    >
                        🔔
                        {unreadCount > 0 && <span className="admin-badge">{unreadCount}</span>}
                    </button>
                    {showNotifications && (
                        <div className="admin-dropdown admin-notification-dropdown">
                            <div className="admin-dropdown-header">
                                <h4>Notifications</h4>
                                <button className="admin-mark-read" onClick={handleMarkAllRead}>Mark all as read</button>
                            </div>
                            <div className="admin-notification-list">
                                {notifications.length === 0 ? (
                                    <div className="admin-notification-item">
                                        <p>No notifications yet</p>
                                    </div>
                                ) : (
                                    notifications.map((notif) => (
                                        <div
                                            key={notif.id}
                                            className={`admin-notification-item ${!notif.is_read ? 'unread' : ''}`}
                                            onClick={() => handleNotificationClick(notif)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <p>{notif.title}</p>
                                            <span className="admin-time">{timeAgo(notif.created_at)}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="admin-dropdown-footer">
                                <a href="/admin/notifications">View all notifications</a>
                            </div>
                        </div>
                    )}
                </div>

                <div className="admin-profile-wrapper">
                    <button
                        className="admin-profile-btn"
                        onClick={() => setShowProfile(!showProfile)}
                    >
                        <div className="admin-avatar">{adminData.name ? adminData.name.charAt(0) : 'A'}</div>
                        <div className="admin-profile-info">
                            <span className="admin-profile-name">{adminData.name}</span>
                            <span className="admin-profile-role">{adminData.role}</span>
                        </div>
                        <span className="admin-dropdown-arrow">▼</span>
                    </button>
                    {showProfile && (
                        <div className="admin-dropdown admin-profile-dropdown">
                            <div className="admin-dropdown-item" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
                                <span>👤</span> My Profile
                            </div>
                            <a href="/admin/settings" className="admin-dropdown-item">
                                <span>⚙️</span> Settings
                            </a>
                            <div className="admin-dropdown-divider"></div>
                            <div className="admin-dropdown-item logout" onClick={handleLogoutClick} style={{ cursor: 'pointer' }}>
                                <span>🚪</span> Logout
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;